import { useEffect, useState } from "react";
import {
  adminDirectoryStatus,
  adminDirectoryImportOne,
  adminDirectoryImportAll,
} from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";
const MUTED = "#5b5e69";

export default function AdminDirectoryPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [trade, setTrade] = useState("");
  const [city, setCity] = useState("");
  const [topOnly, setTopOnly] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const s = await adminDirectoryStatus();
      setStatus(s);
      if (!trade && s?.trades?.length) setTrade(s.trades[0].code);
      if (!city && s?.top_cities?.length) setCity(s.top_cities[0]);
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e?.message || "Failed to load status");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runImportOne = async () => {
    if (!trade || !city) return;
    setBusy(true); setMsg(""); setErrMsg("");
    try {
      const r = await adminDirectoryImportOne(trade, city);
      setMsg(r.ok
        ? `Imported ${trade} in ${city} — found ${r.found}, +${r.inserted} new, ${r.updated} updated, ${r.skipped} skipped.`
        : `Failed: ${r.error}`);
      await load();
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e?.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const runImportAll = async (topOnlyFlag) => {
    if (!window.confirm(`Import ALL trades × ${topOnlyFlag ? "top 15 RI cities" : "all 39 RI cities"}? This can take several minutes.`)) return;
    setBusy(true); setMsg(""); setErrMsg("");
    try {
      const r = await adminDirectoryImportAll({ top_cities_only: topOnlyFlag });
      const t = r.totals || {};
      setMsg(`Done. Found ${t.found}, +${t.inserted} new, ${t.updated} updated, ${t.skipped} skipped, ${t.errors} errors across ${r.runs} queries.`);
      await load();
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e?.message || "Bulk import failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading && !status) return <div style={pad}>Loading…</div>;

  return (
    <div style={{ background: BG, minHeight: "100vh", padding: "30px 20px" }} data-testid="admin-directory-page">
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={eyebrow}>NEARNOW · ADMIN</div>
        <h1 style={h1}>RI CONTRACTOR DIRECTORY</h1>
        <p style={{ color: MUTED, marginBottom: 24 }}>
          Ingest RI contractors from Google Places. NearNow-verified partners are always pinned above imported listings on the public <code>/find-a-pro</code> page.
        </p>

        {!status?.google_places_configured && (
          <div style={warnBox} data-testid="google-not-configured">
            ⚠ GOOGLE_PLACES_API_KEY (or GOOGLE_MAPS_API_KEY) not set in <code>backend/.env</code>. Importing won&apos;t work until you add it.
          </div>
        )}

        {status && (
          <div style={statsGrid} data-testid="directory-stats">
            <Stat label="TOTAL LISTINGS" value={status.total} testid="stat-total" />
            <Stat label="VERIFIED PARTNERS" value={status.partners} accent testid="stat-partners" />
            <Stat label="IMPORTED (GOOGLE)" value={status.imports_google} testid="stat-imports" />
            <Stat label="PENDING CLAIMS" value={status.pending_claims} testid="stat-pending" />
          </div>
        )}

        <div style={card}>
          <h2 style={h2}>IMPORT ONE (Trade × City)</h2>
          <p style={{ color: MUTED, fontSize: 13, marginTop: -8 }}>
            Quick spot-import. ~1 Google Places API call, returns up to 60 businesses.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginTop: 10 }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <Label>TRADE</Label>
              <select value={trade} onChange={(e) => setTrade(e.target.value)}
                      data-testid="import-trade-select" style={inputStyle}>
                {status?.trades?.map((t) => (
                  <option key={t.code} value={t.code}>{t.label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <Label>CITY</Label>
              <select value={city} onChange={(e) => setCity(e.target.value)}
                      data-testid="import-city-select" style={inputStyle}>
                {status?.cities?.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <button onClick={runImportOne} disabled={busy || !status?.google_places_configured}
                    data-testid="import-one-button" style={btnPrimary}>
              {busy ? "Importing…" : "IMPORT →"}
            </button>
          </div>
        </div>

        <div style={card}>
          <h2 style={h2}>BULK IMPORT</h2>
          <p style={{ color: MUTED, fontSize: 13, marginTop: -8 }}>
            Runs every enabled trade × every selected city. Bulk = many API calls, keep the tab open.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
            <button onClick={() => runImportAll(true)} disabled={busy || !status?.google_places_configured}
                    data-testid="import-top-button" style={btnPrimary}>
              IMPORT · TOP 15 CITIES (~210 calls)
            </button>
            <button onClick={() => runImportAll(false)} disabled={busy || !status?.google_places_configured}
                    data-testid="import-all-button" style={btnGhost}>
              IMPORT · ALL 39 CITIES (~546 calls)
            </button>
          </div>
        </div>

        {msg && <div style={okBox} data-testid="import-result-ok">{msg}</div>}
        {errMsg && <div style={warnBox} data-testid="import-result-err">{errMsg}</div>}

        {status?.by_trade && (
          <div style={card}>
            <h2 style={h2}>COUNTS BY TRADE</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
              {Object.entries(status.by_trade).map(([code, count]) => {
                const label = status.trades.find((t) => t.code === code)?.label || code;
                return (
                  <div key={code} style={tradeRow} data-testid={`trade-count-${code}`}>
                    <span style={{ fontSize: 13 }}>{label}</span>
                    <strong style={{ color: NAVY }}>{count}</strong>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {status?.last_import_run && (
          <div style={card}>
            <h2 style={h2}>LAST BULK RUN</h2>
            <pre style={{ background: "#F1F3F6", padding: 12, borderRadius: 8, fontSize: 12, overflow: "auto" }}>
              {JSON.stringify(status.last_import_run, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, accent, testid }) => (
  <div style={{ background: "#fff", border: `2px solid ${accent ? GOLD : "#e5e7ee"}`, borderRadius: 12, padding: 18 }} data-testid={testid}>
    <div style={{ color: MUTED, fontSize: 11, letterSpacing: 2, fontWeight: 700 }}>{label}</div>
    <div style={{ color: NAVY, fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 40, letterSpacing: 2, marginTop: 6 }}>{value ?? "–"}</div>
  </div>
);
const Label = ({ children }) => (
  <div style={{ color: NAVY, fontSize: 10, letterSpacing: 2, fontWeight: 700, marginBottom: 5 }}>{children}</div>
);

const pad = { padding: 40, textAlign: "center" };
const eyebrow = { color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 3, color: NAVY, fontSize: 44, margin: "4px 0 10px" };
const h2 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, color: NAVY, fontSize: 22, margin: "0 0 14px" };
const card = { background: "#fff", border: "1px solid #e5e7ee", borderRadius: 12, padding: 20, marginTop: 20 };
const statsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: 8 };
const inputStyle = { width: "100%", padding: "12px 14px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 15, outline: "none", boxSizing: "border-box", background: "#fff" };
const btnPrimary = { background: GOLD, color: NAVY, border: "none", padding: "14px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 14, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" };
const btnGhost = { background: "transparent", color: NAVY, border: `1.5px solid ${NAVY}`, padding: "14px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 14, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" };
const warnBox = { background: "#FFF5E5", color: "#8a4b00", padding: "12px 16px", borderRadius: 10, marginTop: 12, fontSize: 14 };
const okBox = { background: "#E7F5EE", color: "#0F5132", padding: "12px 16px", borderRadius: 10, marginTop: 12, fontSize: 14 };
const tradeRow = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: BG, borderRadius: 8 };
